import { useEffect, useState } from 'react'
import { ApiError, bookingApi, type PublicClubSummary } from '@/api/client'

function errMsg(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Не удалось загрузить список клубов.'
}

/** Инициалы клуба для запасного логотипа (если URL не задан). */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '')
  return letters.join('') || '8'
}

function ClubCard({
  club,
  onPick
}: {
  club: PublicClubSummary
  onPick: (slug: string) => void
}): React.JSX.Element {
  const place = [club.city, club.address].filter(Boolean).join(', ')
  return (
    <button type="button" onClick={() => onPick(club.slug)} className="club-card">
      <div className="club-head">
        {club.logoUrl ? (
          <img className="club-logo" src={club.logoUrl} alt="" loading="lazy" />
        ) : (
          <div className="club-logo club-logo--mono">{initials(club.name)}</div>
        )}
        <div className="club-headtext">
          <div className="club-name">{club.name}</div>
          {place && <div className="club-place">{place}</div>}
        </div>
      </div>

      {club.description && <p className="club-desc">{club.description}</p>}

      <div className="club-meta">
        {club.gameTypes.length > 0 && (
          <div className="kinds">
            {club.gameTypes.map((g) => (
              <span key={g.code} className="kind-chip">
                <span
                  className={`kind-ball ${g.code}`}
                  style={g.color ? { background: g.color } : undefined}
                />
                {g.name}
              </span>
            ))}
          </div>
        )}
        <span className="club-hours">
          {club.todayHours
            ? `Сегодня ${club.todayHours.open}–${club.todayHours.close}`
            : 'Сегодня выходной'}
        </span>
      </div>

      <span className="club-cta">Забронировать →</span>
    </button>
  )
}

export function HomePage({
  onPick,
  notTelegram
}: {
  onPick: (slug: string) => void
  notTelegram?: boolean
}): React.JSX.Element {
  const [clubs, setClubs] = useState<PublicClubSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    bookingApi
      .listClubs()
      .then((list) => alive && setClubs(list))
      .catch((e) => alive && setError(errMsg(e)))
    return () => {
      alive = false
    }
  }, [])

  return (
    <div className="page">
      <div className="shell">
        <div className="brand">
          <div className="logo">
            8<b>Ball</b>
          </div>
          <div className="sub">Онлайн-бронирование стола</div>
        </div>

        {notTelegram && (
          <div className="tg-note">Откройте это приложение через Telegram-бота бронирования.</div>
        )}

        {error && (
          <div className="card">
            <div className="card-body">
              <p className="empty">{error}</p>
            </div>
          </div>
        )}

        {!error && clubs === null && (
          <div className="center">
            <div className="spinner" />
          </div>
        )}

        {!error && clubs !== null && clubs.length === 0 && (
          <div className="card">
            <div className="card-body">
              <p className="empty">Пока нет клубов, доступных для онлайн-бронирования.</p>
            </div>
          </div>
        )}

        {!error && clubs !== null && clubs.length > 0 && (
          <div className="club-grid">
            {clubs.map((c) => (
              <ClubCard key={c.slug} club={c} onPick={onPick} />
            ))}
          </div>
        )}

        <div className="footer-note">Выберите клуб, чтобы забронировать стол онлайн</div>
      </div>
    </div>
  )
}
